import { getDefaultSearcherContentTypesForSite } from '@/modules/searcher/config/default-seracher-content-types';
import { getContentEditorPermissions } from '@/modules/authorization/config';
import { DASHBOARD_FEATURE, DAYPARTING_FEATURE } from '@/features';
import store from '@/store';
import merge from 'lodash/merge';
import { LOGIN_PAGE_NAME } from '@/modules/authentication/config/login-routes';
import { getStaffHomepage } from '@/modules/staff/config/homepage';

const routes = [
  {
    path: '/auth',
    name: LOGIN_PAGE_NAME,
    component: () => import('@/layouts/Empty'),
    props: () => ({ customClassName: 'fill-height nx-primary--lighten-5' }),
    children: [
      {
        path: ':page',
        name: 'auth:page',
        component: () => import('@/modules/authentication/pages/AuthPage'),
        props: route => ({ ...route.query, page: route.params.page }),
      },
    ],
  },
  {
    path: '/login',
    name: 'login',
    redirect: {
      name: LOGIN_PAGE_NAME,
    },
  },
  {
    path: '/not-authorized',
    component: () => import('@/layouts/Empty'),
    children: [
      {
        path: ':toURL?',
        name: 'unauthorized',
        props: true,
        component: () => import('@/modules/authorization/pages/UnauthorizedPage'),
      },
    ],
  },
  {
    path: '/backstage',
    component: () => import('@/layouts/Empty'),
    children: [
      {
        path: '',
        name: 'backstage',
        component: () => import('@/modules/backstage/pages/BackstagePage'),
      },
    ],
    beforeEnter: (to, from, next) => {
      if (!store.getters['authentication/staffIsLogged']) {
        return next({ name: 'unauthorized' });
      }

      if (!store.getters.loggedStaff) {
        return next({ name: 'unauthorized' });
      }

      if (store.getters.loggedStaff.id === 1 // admin
        || store.getters.loggedStaff.type === 1 // StaffInterface::STAFF_TYPE_BITBAN
      ) {
        return next();
      }

      return next({ name: 'unauthorized' });
    },
  },
  {
    path: '/:site',
    name: 'site',
    component: () => import('@/layouts/Default'),
    props(route) {
      // Toast params
      return { toast: route.query.toast || null };
    },
    children: [
      {
        path: 'test',
        component: () => import('@/layouts/Empty'),
        children: [
          {
            path: '',
            name: 'test',
            component: () => import('@/pages/TestPage'),
          },
        ],
      },
      {
        // Default site Route
        path: '',
        name: 'default',
        beforeEnter: (to, from, next) => {
          const staffHomepage = getStaffHomepage();
          // If the current staff has a saved homepage use it as default
          if (staffHomepage) {
            // Keep params and query from router.
            staffHomepage.params = merge({}, staffHomepage.params, to.params);
            staffHomepage.query = merge({}, staffHomepage.query, to.query);
            return next(staffHomepage);
          }

          const params = {
            entity: 'content',
          };
          if (to.params.site) {
            params.site = to.params.site;
          }

          return next({
            name: 'searcher',
            params,
            query: {
              type: getDefaultSearcherContentTypesForSite(to.params.site),
            },
          });
        },
      },
      {
        // Showroom
        path: 'showroom',
        name: 'showroom',
        component: () => import('@/modules/showroom/pages/ShowroomPage'),
        props: true,
      },
      {
        // Dashboard
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/modules/dashboard/pages/DashboardPage'),
        props: true,
        meta: {
          feature: DASHBOARD_FEATURE,
        },
      },
      {
        // Search
        path: 'searcher/:entity',
        name: 'searcher',
        component: () => import('@/modules/searcher/pages/SearcherPage'),
        props: true,
      },
      // Staff profile page
      {
        path: 'profile',
        name: 'profile',
        component: () => import('@/modules/staff/pages/ProfilePage'),
      },
      // Standalone live messages editor
      {
        path: 'content/editor/:type/:id/standalone',
        component: () => import('@/modules/live-messages/components/StandaloneLiveEditor'),
        name: 'content.liveStandalone',
        props: route => ({ id: parseInt(route.params.id, 10), type: route.params.type }),
        meta: {
          scrollToTop: true,
          permissions: getContentEditorPermissions,
        },
      },
      // Content Editor
      {
        path: 'content/editor/:type/:id/:channel?/:relativeVersionId?',
        component: () => import('@/modules/content/editor/pages/EditorPage'),
        name: 'content.editor',
        props(route) {
          const type = route.params.type;
          const id = parseInt(route.params.id, 10);
          const relativeVersionId = route.params.relativeVersionId
            ? parseInt(route.params.relativeVersionId, 10)
            : null;
          const language = route.query.language || null;
          const channel = route.params.channel || null;
          return {
            type,
            id,
            relativeVersionId,
            language,
            channel,
          };
        },
        meta: {
          scrollToTop: true,
          permissions: getContentEditorPermissions,
        },
      },
      // Board editor
      {
        path: 'board/editor/:id/composition/:compositionRelativeVersionId?',
        component: () => import('@/modules/board/editor/pages/BoardEditorPage'),
        props: route => ({
          boardId: parseInt(route.params.id, 10),
          compositionRelativeVersionId: route.params.compositionRelativeVersionId
            ? parseInt(route.params.compositionRelativeVersionId, 10)
            : null,
          safeMode: !isNaN(route.query.safeMode) ? !!parseInt(route.query.safeMode, 10) : true,
        }),
        name: 'board.editor',
      },
      {
        path: 'composition/editor/:id/metadata',
        component: () => import('@/modules/board/editor/pages/MetadataEditorPage'),
        props: route => ({ id: parseInt(route.params.id, 10), entity: 'composition' }),
        name: 'composition.metadata',
      },
      {
        path: 'board/editor/:id/metadata',
        component: () => import('@/modules/board/editor/pages/MetadataEditorPage'),
        props: route => ({ id: parseInt(route.params.id, 10), entity: 'board' }),
        name: 'board.metadata',
      },
      {
        path: 'board/editor/:id/dayparting',
        component: () => import('@/modules/board/editor/pages/DaypartingListPage'),
        props: route => ({
          boardId: parseInt(route.params.id, 10),
        }),
        meta: {
          feature: DAYPARTING_FEATURE,
        },
        name: 'board.dayparting',
      },
      {
        path: 'board/dayparting/editor/:id',
        name: 'dayparting.editor',
        component: () => import('@/modules/board/dayparting/EditorPage'),
        props: route => ({
          blockId: parseInt(route.params.id, 10),
          refBoard: route.query.refBoard ? parseInt(route.query.refBoard, 10) : null,
        }),
      },
      // Composition editor
      {
        path: 'composition/editor/:compositionId/:compositionRelativeVersionId?',
        component: () => import('@/modules/composition/pages/CompositionEditorPage'),
        props: route => ({
          compositionId: parseInt(route.params.compositionId, 10),
          compositionRelativeVersionId: route.params.compositionRelativeVersionId
            ? parseInt(route.params.compositionRelativeVersionId, 10)
            : null,
          safeMode: route.query.safeMode ? !!parseInt(route.query.safeMode, 10) : true,
        }),
        name: 'composition.editor',
      },

      // Entity editor
      {
        path: ':entity/:id/:tab([A-Za-z]+)?/:relativeVersionId(\\d+)?',
        component: () => import('@/modules/entity/pages/EntityEditorPage'),
        name: 'entity.editor',
        props: route => ({
          entity: route.params.entity,
          id: parseInt(route.params.id, 10),
          relativeVersionId: route.params.relativeVersionId ? parseInt(route.params.relativeVersionId, 10) : null,
          tab: route.params.tab || 'editor',
        }),
        beforeEnter: (to, from, next) => {
          const entity = to.params.entity;
          if (!store.getters.getEntityConfig(entity)) {
            // The entity doesn't exist. Cancel the navigation
            return next({ name: 'notFound', path: to.path });
          }
          if (store.getters.availableContentTypes.includes(entity)
            || entity === 'board') {
            // The entity is a content or a board. Cancel the navigation
            return next({ name: 'notFound', path: to.path });
          }
          return next();
        },
      },
      // External endpoints
      {
        path: 'external/:url',
        name: 'external',
        beforeEnter: (to) => {
          window.location.href = to.params.url;
        },
      },
    ],
  },
  {
    path: '',
    name: 'home',
    redirect: {
      name: 'default',
    },
  },
  {
    // Redirect all unknown routes
    path: '*',
    component: () => import('@/layouts/Empty'),
    children: [
      {
        path: '',
        name: 'notFound',
        component: () => import('@/pages/NotFound'),
      },
      {
        path: 'site-mismatch',
        name: 'siteMismatch',
        props: true,
        component: () => import('@/pages/SiteMismatchError'),
      },
    ],
  },
];

export default routes;
